import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Ellipsis, KeyRound, Undo2, Users } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import {
  everyInsideQuery,
  everyOccupancyQuery,
  insideQuery,
  occupancyQuery,
  settingsQuery,
  useLeave,
  useVisitKey,
  type Locker,
  type Visit,
} from '@/api/access'
import { clientName, clientNamesQuery } from '@/api/passes'
import { branchesQuery } from '@/api/queries'
import { EntryShell, type EntryContext } from '@/app/access/entry-shell'
import { MoreFooter } from '@/app/goods/more-footer'
import { useCompanyCaller } from '@/app/company'
import { clockTime, durationText, heldByRenter, minutesBetween, suggestLocker } from '@/domain/access'
import { clientAbilities } from '@/domain/client-abilities'
import { errorText } from '@/domain/errors'
import { canonicalZone, DEFAULT_ZONE } from '@/domain/timezones'
import { t } from '@/i18n'
import { Button } from '@/ui/button'
import { Avatar } from '@/ui/compact-row'
import { DataList, type DataColumn } from '@/ui/data-list'
import { Field } from '@/ui/field'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog, RowMenu, RowMenuItem, SidePanel } from '@/ui/overlays'
import { EmptyState } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { LoadingBlock } from '@/ui/spinner'
import { StatTile, StatTiles } from '@/ui/stat-tile'
import { useNow } from '@/app/access/use-now'

export const Route = createFileRoute('/_app/c/$orgId/entry/inside')({
  component: Inside,
})

function Inside() {
  const { orgId } = Route.useParams()
  return (
    <EntryShell orgId={orgId} tab="inside" everyClub allowed={(can) => can.visits}>
      {(ctx) => (ctx.everyClub ? <EveryClubInside {...ctx} /> : <InsideScreen key={ctx.branch.id} {...ctx} />)}
    </EntryShell>
  )
}

type KeyOut = Readonly<{ number: string; visitId: string; clientId: string | null }>

/// «Зараз у залі»: the live count, who is inside since when, and the keys.
/// Polled every 15 s. «Вийшов» and the key actions for `checkin ≥ edit`.
/// Under it, the keys that left with somebody whose visit is already over
/// (a turnstile exit, an auto-close) — the desk takes them back here.
function InsideScreen({ orgId, branch, timeZone, can }: EntryContext) {
  const now = useNow(30_000)
  const occupancy = useQuery(occupancyQuery(orgId, branch.id))
  const visits = useQuery(insideQuery(orgId, branch.id))
  const settings = useQuery(settingsQuery(orgId, branch.id))
  const keysMode = settings.data?.lockerMode === 'keys'
  const inside = (visits.data ?? []).filter((visit) => visit.inside)
  const insideIds = new Set(inside.map((visit) => visit.id))
  // A key out with the person who rents its locker is theirs for the period,
  // not a debt (specs/access.md §10b).
  const notReturned: readonly KeyOut[] = keysMode
    ? (settings.data?.lockers ?? [])
        .filter(
          (l): l is Locker & { visitId: string } =>
            l.state === 'issued' && l.visitId !== null && !insideIds.has(l.visitId) && !heldByRenter(l),
        )
        .map((l) => ({ number: l.number, visitId: l.visitId, clientId: l.clientId }))
    : []
  const ids = [...new Set([...inside.map((v) => v.clientId), ...notReturned.flatMap((k) => (k.clientId ? [k.clientId] : []))])].sort()
  const names = useQuery(clientNamesQuery(orgId, ids))
  const known = new Map((names.data ?? []).map((client) => [client.id, clientName(client)]))
  const who = (clientId: string | null) => (clientId ? known.get(clientId) : undefined) ?? t.passes.sold.unknownClient

  const leave = useLeave(orgId, branch.id)
  const key = useVisitKey(orgId, branch.id)
  const [issuing, setIssuing] = useState<Visit | null>(null)
  const [losing, setLosing] = useState<{ visitId: string; number: string } | null>(null)
  const [news, setNews] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const s = t.access.inside

  const act = (promise: Promise<unknown>, done?: string) => {
    setFailure(null)
    setNews(null)
    promise.then(
      () => (done ? setNews(done) : undefined),
      (error: unknown) => setFailure(errorText(error)),
    )
  }

  const columns: readonly DataColumn<Visit>[] = [
    {
      key: 'client',
      header: s.columns.client,
      width: 'minmax(0,1.4fr)',
      cell: (visit) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <Avatar name={who(visit.clientId)} className="size-6 text-[10px] max-sm:hidden" />
          <span className="truncate">{who(visit.clientId)}</span>
        </span>
      ),
    },
    {
      key: 'since',
      header: s.columns.since,
      width: 'minmax(0,1fr)',
      cell: (visit) => (
        <span className="tabular">
          {s.sinceLine(clockTime(visit.enteredAt, timeZone), durationText(minutesBetween(visit.enteredAt, now)))}
        </span>
      ),
    },
    {
      key: 'method',
      header: s.columns.method,
      width: 'minmax(0,0.7fr)',
      hideBelow: 'md',
      cell: (visit) => t.access.methods[visit.method] ?? visit.method,
    },
    ...(keysMode
      ? [
          {
            key: 'key',
            header: s.columns.key,
            width: '72px',
            mobile: 'trailing',
            cell: (visit: Visit) =>
              visit.keyOut && visit.lockerNumber ? (
                <span className="font-semibold text-text tabular">{visit.lockerNumber}</span>
              ) : (
                <span className="text-muted">{s.noKey}</span>
              ),
          } satisfies DataColumn<Visit>,
        ]
      : []),
    ...(can.admit
      ? [
          {
            key: 'leave',
            header: '',
            width: '112px',
            align: 'end',
            mobile: 'trailing',
            cell: (visit: Visit) => (
              <Button
                compact
                variant="secondary"
                busy={leave.isPending && leave.variables === visit.id}
                onClick={() => act(leave.mutateAsync(visit.id), s.leftBanner(who(visit.clientId)))}
              >
                {s.left}
              </Button>
            ),
          } satisfies DataColumn<Visit>,
        ]
      : []),
  ]

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap gap-x-10 gap-y-4">
        <Figure label={s.now} value={occupancy.data?.inside} />
        {keysMode ? <Figure label={s.keysOut} value={occupancy.data?.keysOut} /> : null}
      </div>

      {news ? <InlineBanner kind="done" message={news} /> : null}
      {failure ? <InlineBanner kind="error" message={failure} /> : null}

      {visits.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : visits.isError && !visits.data ? (
        <InlineBanner kind="error" message={errorText(visits.error)} action={{ label: t.app.retry, onClick: () => void visits.refetch() }} />
      ) : inside.length === 0 ? (
        <EmptyState icon={<Users />} title={s.empty} message={s.emptyMessage} />
      ) : (
        <DataList
          label={t.access.tabs.inside}
          columns={columns}
          rows={inside}
          rowKey={(visit) => visit.id}
          rowActions={
            can.admit && keysMode
              ? (visit) => (
                  <RowMenu label={`${t.app.more}: ${who(visit.clientId)}`} trigger={<Ellipsis className="size-[18px]" />}>
                    {visit.keyOut && visit.lockerNumber ? (
                      <>
                        <RowMenuItem
                          icon={<Undo2 className="size-4" />}
                          onSelect={() => act(key.mutateAsync({ visitId: visit.id, body: { action: 'return' } }))}
                        >
                          {s.keyReturned}
                        </RowMenuItem>
                        <RowMenuItem
                          destructive
                          icon={<KeyRound className="size-4" />}
                          onSelect={() => setLosing({ visitId: visit.id, number: visit.lockerNumber ?? '' })}
                        >
                          {s.keyLost}
                        </RowMenuItem>
                      </>
                    ) : (
                      <RowMenuItem icon={<KeyRound className="size-4" />} onSelect={() => setIssuing(visit)}>
                        {s.issueKey}
                      </RowMenuItem>
                    )}
                  </RowMenu>
                )
              : undefined
          }
        />
      )}

      {notReturned.length > 0 ? (
        <section className="flex flex-col gap-2">
          <SectionHeader title={s.notReturned} count={notReturned.length} />
          <DataList
            label={s.notReturned}
            rows={notReturned}
            rowKey={(row) => row.number}
            columns={[
              {
                key: 'key',
                header: s.columnsKeys.key,
                width: '88px',
                cell: (row) => <span className="tabular">{row.number}</span>,
              },
              { key: 'client', header: s.columnsKeys.client, width: 'minmax(0,1fr)', cell: (row) => who(row.clientId) },
              ...(can.admit
                ? [
                    {
                      key: 'actions',
                      header: '',
                      width: '220px',
                      align: 'end',
                      mobile: 'trailing',
                      cell: (row: KeyOut) => (
                        <span className="flex gap-1.5">
                          <Button
                            compact
                            variant="secondary"
                            onClick={() => act(key.mutateAsync({ visitId: row.visitId, body: { action: 'return' } }))}
                          >
                            {s.returned}
                          </Button>
                          <Button compact variant="quiet" onClick={() => setLosing({ visitId: row.visitId, number: row.number })}>
                            {s.lost}
                          </Button>
                        </span>
                      ),
                    } satisfies DataColumn<KeyOut>,
                  ]
                : []),
            ]}
          />
          <p className="px-4 text-caption text-muted">{s.notReturnedFootnote}</p>
        </section>
      ) : null}

      {issuing ? (
        <IssueKeyPanel
          visit={issuing}
          name={who(issuing.clientId)}
          suggestion={suggestLocker(settings.data?.lockers ?? [], issuing.clientId)}
          busy={key.isPending}
          error={key.isError ? errorText(key.error) : null}
          onClose={() => {
            setIssuing(null)
            key.reset()
          }}
          onIssue={(number) =>
            key.mutate({ visitId: issuing.id, body: { action: 'issue', number } }, { onSuccess: () => setIssuing(null) })
          }
        />
      ) : null}

      <ConfirmDialog
        open={losing !== null}
        onOpenChange={(open) => {
          if (!open) setLosing(null)
        }}
        title={losing ? s.lostTitle(losing.number) : ''}
        message={s.lostMessage}
        confirmLabel={s.lostConfirm}
        busy={key.isPending}
        error={key.isError && losing ? <InlineBanner kind="error" message={errorText(key.error)} /> : null}
        onConfirm={() => {
          if (!losing) return
          key.mutate({ visitId: losing.visitId, body: { action: 'lost' } }, { onSuccess: () => setLosing(null) })
        }}
      />
    </div>
  )
}

/// Names for a list longer than one clients request takes (200 ids).
const useClientNames = (orgId: string, ids: readonly string[]): ReadonlyMap<string, string> => {
  const sorted = [...new Set(ids)].sort()
  const chunks = Array.from({ length: Math.ceil(sorted.length / 200) }, (_, i) => sorted.slice(i * 200, (i + 1) * 200))
  const answers = useQueries({ queries: chunks.map((chunk) => clientNamesQuery(orgId, chunk)) })
  return new Map(answers.flatMap((answer) => answer.data ?? []).map((client) => [client.id, clientName(client)]))
}

/// «Зараз у залі» for every club the caller works in (specs/access.md §7.2):
/// the total, each club's own count — its tile opens that club — and
/// everybody inside, newest first, with their club, a page at a time. Polled
/// like one club. Read-only on purpose: «Вийшов» and the keys stay in the
/// club's own view, where the desk that hands the keys out works. A row
/// opens the client's card — their visits are there (§7.1).
function EveryClubInside({ orgId, branches, pickClub }: EntryContext) {
  const now = useNow(30_000)
  const { access } = useCompanyCaller(orgId)
  const openCard = clientAbilities(access).see
  // Every club the company has, archived too: a visit names the club it was in.
  const clubs = useQuery(branchesQuery(orgId))
  const occupancy = useQuery(everyOccupancyQuery(orgId))
  const visits = useInfiniteQuery(everyInsideQuery(orgId))
  const rows = (visits.data?.pages ?? []).flatMap((page) => page.visits).filter((visit) => visit.inside)
  const names = useClientNames(orgId, rows.map((visit) => visit.clientId))
  const who = (clientId: string) => names.get(clientId) ?? t.passes.sold.unknownClient
  const club = (branchId: string) => (clubs.data ?? []).find((b) => b.id === branchId)
  const zoneOf = (branchId: string) => canonicalZone(club(branchId)?.timezone ?? branches[0]?.timezone ?? DEFAULT_ZONE)
  const counts = new Map((occupancy.data?.branches ?? []).map((count) => [count.branchId, count]))
  const anyKey = rows.some((visit) => visit.keyOut && visit.lockerNumber)
  const s = t.access.inside

  const columns: readonly DataColumn<Visit>[] = [
    {
      key: 'client',
      header: s.columnsEvery.client,
      width: 'minmax(0,1.4fr)',
      cell: (visit) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <Avatar name={who(visit.clientId)} className="size-6 text-[10px] max-sm:hidden" />
          <span className="truncate">{who(visit.clientId)}</span>
        </span>
      ),
    },
    {
      key: 'club',
      header: s.columnsEvery.club,
      width: 'minmax(0,1fr)',
      cell: (visit) => club(visit.branchId)?.name ?? t.access.branch,
    },
    {
      key: 'since',
      header: s.columnsEvery.since,
      width: 'minmax(0,1fr)',
      cell: (visit) => (
        <span className="tabular">
          {s.sinceLine(clockTime(visit.enteredAt, zoneOf(visit.branchId)), durationText(minutesBetween(visit.enteredAt, now)))}
        </span>
      ),
    },
    {
      key: 'method',
      header: s.columnsEvery.method,
      width: 'minmax(0,0.7fr)',
      hideBelow: 'md',
      mobile: 'hidden',
      cell: (visit) => t.access.methods[visit.method] ?? visit.method,
    },
    ...(anyKey
      ? [
          {
            key: 'key',
            header: s.columnsEvery.key,
            width: '72px',
            mobile: 'trailing',
            // No key is an empty cell, not a column of dashes: most of the
            // company's clubs hand none out.
            cell: (visit: Visit) =>
              visit.keyOut && visit.lockerNumber ? (
                <span className="font-semibold text-text tabular">{visit.lockerNumber}</span>
              ) : null,
          } satisfies DataColumn<Visit>,
        ]
      : []),
  ]

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <Figure label={s.everyNow} value={occupancy.data?.inside} />
        <StatTiles className="grid-cols-2 sm:grid-cols-3 xl:grid-cols-4">
          {branches.map((branch) => {
            const count = counts.get(branch.id)
            const inside = count?.inside ?? 0
            return (
              <StatTile
                key={branch.id}
                compact
                label={branch.name}
                value={occupancy.data ? String(inside) : '—'}
                note={count?.keysOut ? s.clubKeys(count.keysOut) : undefined}
                onOpen={() => pickClub(branch.id)}
                openLabel={s.openClub(branch.name, inside)}
              />
            )
          })}
        </StatTiles>
      </div>

      {visits.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : visits.isError && !visits.data ? (
        <InlineBanner kind="error" message={errorText(visits.error)} action={{ label: t.app.retry, onClick: () => void visits.refetch() }} />
      ) : rows.length === 0 ? (
        <EmptyState icon={<Users />} title={s.emptyEvery} message={s.emptyEveryMessage} />
      ) : (
        <DataList
          label={t.access.everyClub}
          columns={columns}
          rows={rows}
          rowKey={(visit) => visit.id}
          rowTarget={
            openCard
              ? (visit) => ({ to: '/c/$orgId/clients/$clientId', params: { orgId, clientId: visit.clientId } })
              : undefined
          }
          rowLabel={(visit) => `${t.clients.open}: ${who(visit.clientId)}`}
          // A footer only once there is more than a page: an empty one is a
          // stray line under the list.
          footer={
            visits.hasNextPage || (visits.data?.pages.length ?? 0) > 1 ? (
              <MoreFooter
                shown={rows.length}
                pages={visits.data?.pages.length ?? 0}
                hasMore={visits.hasNextPage}
                loading={visits.isFetchingNextPage}
                onMore={() => void visits.fetchNextPage()}
              />
            ) : undefined
          }
        />
      )}
      <p className="px-4 text-caption text-muted">{s.everyFootnote}</p>
    </div>
  )
}

/// A live number with its label — the one figure the page leads with.
const Figure = ({ label, value }: { label: string; value: number | undefined }) => (
  <div className="flex flex-col">
    <span className="text-caption font-medium text-muted">{label}</span>
    <span className="text-[44px] leading-[52px] font-bold tracking-[-0.02em] text-text">{value ?? '—'}</span>
  </div>
)

function IssueKeyPanel({
  visit,
  name,
  suggestion,
  busy,
  error,
  onClose,
  onIssue,
}: {
  visit: Visit
  name: string
  suggestion: string | null
  busy: boolean
  error: string | null
  onClose: () => void
  onIssue: (number: string) => void
}) {
  const s = t.access.inside
  const [number, setNumber] = useState(suggestion ?? '')
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (number.trim()) onIssue(number.trim())
  }
  return (
    <SidePanel
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={s.issueKey}
      description={name}
      onSubmit={submit}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t.app.cancel}
          </Button>
          <Button type="submit" variant="primary" busy={busy} disabled={!number.trim()}>
            {s.issueSubmit}
          </Button>
        </>
      }
    >
      <div key={visit.id} className="flex flex-col gap-5">
        <Field
          data-autofocus=""
          label={s.issueField}
          value={number}
          hint={suggestion ? t.access.desk.keyHint : t.access.desk.noFreeKey}
          onChange={(event) => setNumber(event.target.value)}
        />
        {error ? <InlineBanner kind="error" message={error} /> : null}
      </div>
    </SidePanel>
  )
}
